import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrainingStats } from '@/hooks/useTrainingStats';

interface SuccessRateBreakdownProps {
  stats: TrainingStats;
}

export function SuccessRateBreakdown({ stats }: SuccessRateBreakdownProps) {
  const getColor = (rate: number) => {
    if (rate >= 75) return 'hsl(var(--teen-green))';
    if (rate >= 60) return 'hsl(var(--teen-gold))';
    return 'hsl(var(--teen-orange))';
  };

  const mistakeTypeData = Object.entries(stats.successByMistakeType)
    .map(([type, data]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      rate: data.rate,
      attempts: data.attempts,
      correct: data.correct
    }))
    .sort((a, b) => b.rate - a.rate);

  const categoryData = Object.entries(stats.successByCategory)
    .map(([category, data]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      rate: data.rate,
      attempts: data.attempts,
      correct: data.correct
    }))
    .sort((a, b) => b.rate - a.rate);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {data.correct}/{data.attempts} correct ({data.rate}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Success by Mistake Type</h3>
        {mistakeTypeData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={mistakeTypeData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis 
                dataKey="name" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="rate" radius={[8, 8, 0, 0]}>
                {mistakeTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColor(entry.rate)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No data available yet
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Success by Category</h3>
        {categoryData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis 
                dataKey="name" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                label={{ value: 'Success Rate (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="rate" radius={[8, 8, 0, 0]}>
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getColor(entry.rate)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No data available yet
          </div>
        )}
      </Card>
    </div>
  );
}
